import { AxiosResponse } from 'axios'

import ClientSDK from '../../../App/ClientSDK'
import { SurveyTriggerType, SurveyTrigger } from '../screens/Components/EditDescriptionCard/EditDescriptionCard'
import { AddQuestion, DeleteQuestion, UpdateSurveyDescription, UpdateSurveyMailSubject, UpdateSurveyName } from '../Store/ActionTypes'


import {
    APISurveyObject,
  ISurveyQuestion,
  SurveyObject,
} from './ISurvey'
import Transformer from './Transformer'

class Client {
  static getSurveys = (): Promise<SurveyObject[]> =>
    ClientSDK.instance()
      .get<{surveys: APISurveyObject[]}>(`customers/surveys`)
      .then((response): SurveyObject[] => {
        return response.data.surveys.map((data) =>
          Transformer.transform(data),
        )
      })

  static getSurvey = (surveyId: string): Promise<SurveyObject> =>
    ClientSDK.instance()
      .get<APISurveyObject>(`customers/surveys/${surveyId}`)
      .then((response): SurveyObject => {
        return Transformer.transform(response.data)
      })

  static deleteSurvey = (surveyId: string): Promise<AxiosResponse> =>
    ClientSDK.instance().delete(`customers/surveys/delete/survey/${surveyId}`)

  static addSurvey = (
    name: string,
    description: string,
    mailSubject: string,
    establishmentId: number,
  ): Promise<{ id: string }> =>
    ClientSDK.instance()
      .post(`customers/surveys`, {
        name,
        description,
        mail_subject: mailSubject,
        establishment_id: establishmentId,
      })
      .then((response: AxiosResponse) => ({
        id: response.data.id,
      }))

  static updateSurveyName = (
    surveyId: string,
    name: string,
  ): Promise<UpdateSurveyName> =>
    ClientSDK.instance()
      .patch(`customers/surveys/${surveyId}/name`, { name })
      .then(() => {
        return {
          surveyId,
          name,
        }
      })

  static updateSurveyLanguage = (
    surveyId: string,
    language: string,
  ): Promise<{ surveyId: string, language: string }> =>
    ClientSDK.instance()
      .patch(`customers/surveys/${surveyId}/language`, { language })
      .then(() => {
        return {
          surveyId,
          language,
        }
      })

  static updateSurveyDescription = (
    surveyId: string,
    description: string,
  ): Promise<UpdateSurveyDescription> =>
    ClientSDK.instance()
      .patch(`customers/surveys/${surveyId}/description`, { description })
      .then((): UpdateSurveyDescription => {
        return {
          surveyId,
          description,
        }
      })

  static updateSurveyMailSubject = (
    surveyId: string,
    mailSubject: string,
  ): Promise<UpdateSurveyMailSubject> =>
    ClientSDK.instance()
      .patch<{status: 'ok'}>(`customers/surveys/${surveyId}/mailsubject`, {
        mail_subject: mailSubject,
      })
      .then(() => {
        return {
          surveyId,
          mailSubject,
        }
      })

  static updateSurveySendAfterAppointment = (
    surveyId: string,
    send: boolean,
  ): Promise<AxiosResponse> =>
    ClientSDK.instance().post(
      `customers/surveys/${surveyId}/set-send-after-appointment`,
      { send },
    )

  static updateSurveySendBeforeAppointment = (
    surveyId: string,
    days: number,
  ): Promise<AxiosResponse> =>
    ClientSDK.instance().post(
      `customers/surveys/${surveyId}/set-send-before-appointment`,
      {
        send: !isNaN(days),
        days: isNaN(days) ? 1 : days,
      },
    )

  static addQuestion = (question: AddQuestion): Promise<ISurveyQuestion> =>
    ClientSDK.instance()
      .post<{id: string}>(`customers/surveys/${question.surveyId}/questions/open`, {
        question: question.question,
        order: question.order,
        is_multiline: question.isMultiline,
      })
      .then((response): ISurveyQuestion => {
        return {
          id: response.data.id,
          question: question.question,
          type: 'open',
          order: question.order,
          isMultipleChoice: false,
          isMultiline: question.isMultiline,
          choices: [],
        }
      })

  static updateQuestion = (
    question: ISurveyQuestion,
    surveyId: string,
  ): Promise<any> => {
    return ClientSDK.instance().put(
      `customers/surveys/${surveyId}/questions/${question.id}`,
      Transformer.reverseTransformQuestion(question),
    )
  }

  static deleteQuestion = (
    question: DeleteQuestion,
  ): Promise<DeleteQuestion> =>
    ClientSDK.instance()
      .delete<{[key: string]: 'deleted'}>(
        `customers/surveys/${question.surveyId}/questions/${question.questionId}`,
      )
      .then(() => {
        return {
          surveyId: question.surveyId,
          questionId: question.questionId,
        }
      })

  static updateQuestionsOrder = (
    questions: ISurveyQuestion[],
    question_ids: string[],
    surveyId: string,
  ): Promise<AxiosResponse<{status: 'ok'}>> => 
    ClientSDK.instance().post<{status: 'ok'}>(`customers/surveys/${surveyId}/questions/order`, {
      questions,
      question_ids,
    })

  static createSurveyTrigger = (
    surveyId: string,
    type: SurveyTriggerType,
  ) =>
    {
      const afterObject = {
        type,
        categoryIds: [],
        activityUuids: [],
      }

      const beforeObject = {
        type,
        amount: 0,
      }

      return (
        ClientSDK.instance()
        .post<{ uuid: string }>(`customers/surveys/${surveyId}/triggers`, 
          type === 'send_after_appointment' ? afterObject : beforeObject
        )
        .then((response) => ({
              uuid: response.data.uuid,
              type,
            }))
      )
    }


  static deleteSurveyTrigger = (
    surveyId: string,
    deletedUuid: string,
  ) =>
    ClientSDK.instance().delete(
      `customers/surveys/${surveyId}/triggers/${deletedUuid}`,
    )

  static updateSurveyTrigger = (
    surveyId: string,
    trigger: SurveyTrigger,
  ): Promise<SurveyTrigger> => {
    const data: any = {
      type: trigger.type,
      frequency: trigger.frequency,
    }

    if (trigger.type === 'send_after_appointment') {
      data.activity_uuids = trigger.activityUuids ?? []
      data.category_ids = trigger.categoryIds ?? []
    } else if (trigger.type === 'send_days_before_appointment') {
      data.amount = trigger.amount ?? 1
    }

    return ClientSDK.instance()
      .put(`customers/surveys/${surveyId}/triggers/${trigger.uuid}`, data)
      .then(() => ({ ...trigger } ))
  }
}

export default Client
